import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  KeyboardEvent,
} from "react";
import {
  Box,
  IconButton,
  InputBase,
  Paper,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  Send,
  AttachFile,
  Download,
  InsertPhoto,
} from "@mui/icons-material";
import { useFileService } from "../../../../hooks/api/files";
import { useAuth } from "../../../../hooks/auth";
import { useAlert } from "../../../../hooks/alert";
import { useAppointmentChat } from "../../../../hooks/appointmentChat";
import { AppointmentChatMessage } from "../../../../hooks/appointmentChat/types";
import { FileResponseDto } from "../../../../hooks/api/files/interfaces/fileResponse.dto";
import { downloadBase64File } from "../../utils/downloadBase64File";

type VideoCallParticipants = {
  appointmentId: number;
  patientName: string;
  doctorName: string;
  patientUserId?: number;
  doctorUserId?: number;
};

interface ChatPanelProps {
  appointmentId: number;
  onClose: () => void;
  isDesktop: boolean;
  windowWidth: number;
  windowHeight: number;
  isOpen: boolean;
  participants?: VideoCallParticipants | null;
}

const resolveFileAuthorName = (
  file: FileResponseDto,
  participants?: VideoCallParticipants | null,
) => {
  if (file.authorRole === "doctor" && participants?.doctorName) {
    return participants.doctorName;
  }

  if (file.authorRole === "patient" && participants?.patientName) {
    return participants.patientName;
  }

  if (file.authorUserId && participants?.doctorUserId === file.authorUserId) {
    return participants.doctorName;
  }

  if (file.authorUserId && participants?.patientUserId === file.authorUserId) {
    return participants.patientName;
  }

  return file.author;
};

const isOwnFile = (
  file: FileResponseDto,
  userIdLogged: number,
  uploadedFileIds: Set<number>,
) => uploadedFileIds.has(file.id) || file.authorUserId === userIdLogged;

const isImageFile = (mimeType: string) => mimeType.startsWith("image/");

const FILE_UPLOAD_MESSAGE_PREFIX = "📎 Arquivo enviado:";

const isFileUploadNotification = (text: string) =>
  text.startsWith(FILE_UPLOAD_MESSAGE_PREFIX);

const parseTimestamp = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }

  if (typeof value === "string") {
    const normalized =
      value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;
    const time = new Date(normalized).getTime();
    return Number.isFinite(time) ? time : null;
  }

  const time = new Date(value as Date).getTime();
  return Number.isFinite(time) ? time : null;
};

const buildFileUploadTimestampByDisplayName = (
  messages: AppointmentChatMessage[],
): Map<string, number> => {
  const map = new Map<string, number>();

  messages.forEach((message) => {
    if (!isFileUploadNotification(message.text)) {
      return;
    }

    const displayName = message.text
      .slice(FILE_UPLOAD_MESSAGE_PREFIX.length)
      .trim();
    const timestamp = parseTimestamp(message.createdAt);

    if (displayName && timestamp != null) {
      map.set(displayName, timestamp);
    }
  });

  return map;
};

const resolveFileTimestamp = (
  file: FileResponseDto,
  uploadMessageTimestamps: Map<string, number>,
  clientUploadTimestamp?: number,
): number => {
  const fromApi =
    parseTimestamp(file.created_at) ??
    parseTimestamp((file as FileResponseDto & { createdAt?: string }).createdAt);

  if (fromApi != null) {
    return fromApi;
  }

  if (clientUploadTimestamp != null) {
    return clientUploadTimestamp;
  }

  const fromLegacyMessage = uploadMessageTimestamps.get(file.display_name);
  if (fromLegacyMessage != null) {
    return fromLegacyMessage;
  }

  return Number.MAX_SAFE_INTEGER;
};

const compareTimelineItems = (a: ChatTimelineItem, b: ChatTimelineItem) => {
  const diff = a.timestamp - b.timestamp;
  if (diff !== 0) {
    return diff;
  }

  return a.id.localeCompare(b.id);
};

type ChatTimelineItem =
  | {
      kind: "message";
      id: string;
      timestamp: number;
      message: AppointmentChatMessage;
    }
  | {
      kind: "file";
      id: string;
      timestamp: number;
      file: FileResponseDto;
    };

const buildChatTimeline = (
  messages: AppointmentChatMessage[],
  files: FileResponseDto[],
  clientUploadTimestamps: Map<number, number> = new Map(),
): ChatTimelineItem[] => {
  const items: ChatTimelineItem[] = [];
  const uploadMessageTimestamps = buildFileUploadTimestampByDisplayName(messages);

  messages
    .filter((message) => !isFileUploadNotification(message.text))
    .forEach((message) => {
      items.push({
        kind: "message",
        id: `message-${message.id}`,
        timestamp: parseTimestamp(message.createdAt) ?? Number.MAX_SAFE_INTEGER,
        message,
      });
    });

  files.forEach((file) => {
    items.push({
      kind: "file",
      id: `file-${file.id}`,
      timestamp: resolveFileTimestamp(
        file,
        uploadMessageTimestamps,
        clientUploadTimestamps.get(file.id),
      ),
      file,
    });
  });

  return items.sort(compareTimelineItems);
};

export const ChatPanel: React.FC<ChatPanelProps> = ({
  appointmentId,
  onClose,
  isDesktop,
  windowWidth,
  windowHeight,
  isOpen,
  participants,
}) => {
  const {
    messages,
    isConnected,
    isTyping,
    sendMessage,
    emitTyping,
    emitStopTyping,
  } = useAppointmentChat();

  const [files, setFiles] = useState<FileResponseDto[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [userIdLogged, setUserIdLogged] = useState(0);
  const [userName, setUserName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileIds, setUploadedFileIds] = useState<Set<number>>(new Set());
  const [clientUploadTimestamps, setClientUploadTimestamps] = useState<
    Map<number, number>
  >(new Map());
  const [downloadingFileIds, setDownloadingFileIds] = useState<Set<number>>(new Set());

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { error: showError } = useAlert();
  const { getUserData } = useAuth();
  const { postCreate, getFiles, getFile } = useFileService();
  const getFilesRef = useRef(getFiles);
  getFilesRef.current = getFiles;

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  }, []);

  const timeline = useMemo(
    () => buildChatTimeline(messages, files, clientUploadTimestamps),
    [messages, files, clientUploadTimestamps],
  );

  useEffect(() => {
    const user = getUserData();
    setUserIdLogged(user.id);
    setUserName(user.name);
  }, [getUserData]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      scrollToBottom(timeline.length <= 1 ? "auto" : "smooth");
    });

    return () => cancelAnimationFrame(frameId);
  }, [timeline, isOpen, scrollToBottom]);

  const fetchFiles = useCallback(async () => {
    try {
      const data = await getFilesRef.current(appointmentId);
      if (data) {
        setFiles(data);
        setClientUploadTimestamps((previous) => {
          if (previous.size === 0) {
            return previous;
          }

          const next = new Map(previous);
          data.forEach((file) => {
            if (parseTimestamp(file.created_at) ?? parseTimestamp(file.createdAt)) {
              next.delete(file.id);
            }
          });
          return next.size === previous.size ? previous : next;
        });
      }
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  }, [appointmentId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    fetchFiles();
    const intervalId = window.setInterval(fetchFiles, 5000);

    return () => window.clearInterval(intervalId);
  }, [appointmentId, isOpen, fetchFiles]);

  const handleSendMessage = useCallback(() => {
    if (!newMessage.trim()) {
      return;
    }

    sendMessage(newMessage);
    emitStopTyping();
    setNewMessage("");
  }, [emitStopTyping, newMessage, sendMessage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);

    if (e.target.value.trim()) {
      emitTyping(userName);

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        emitStopTyping();
      }, 2000);
    } else {
      emitStopTyping();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    const formData = new FormData();
    formData.append("url", file.name);
    formData.append("appointmentId", String(appointmentId));
    formData.append("file", file);

    try {
      const response = await postCreate({ body: formData });
      const uploadedFileId = response?.data?.id;

      if (uploadedFileId) {
        const uploadedAt = Date.now();
        setUploadedFileIds((prev) => new Set(prev).add(uploadedFileId));
        setClientUploadTimestamps((prev) =>
          new Map(prev).set(uploadedFileId, uploadedAt),
        );
      }

      await fetchFiles();
    } catch (err) {
      showError("Erro ao enviar arquivo");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDownloadFile = async (file: FileResponseDto) => {
    setDownloadingFileIds((prev) => new Set(prev).add(file.id));
    try {
      const fileData = await getFile(file.id);
      downloadBase64File(fileData, file.mime_type, file.display_name);
    } catch (err) {
      showError("Erro ao baixar arquivo");
    } finally {
      setDownloadingFileIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(file.id);
        return newSet;
      });
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const panelWidth = isDesktop ? Math.min(400, windowWidth * 0.3) : windowWidth;
  const panelHeight = isDesktop ? windowHeight : windowHeight * 0.85;

  const panelStyle: React.CSSProperties = isDesktop
    ? {
        position: "absolute",
        top: 0,
        right: 0,
        width: panelWidth,
        height: panelHeight,
        backgroundColor: "#fff",
        borderLeft: "1px solid #E2E8F0",
        display: isOpen ? "flex" : "none",
        flexDirection: "column",
        zIndex: 200,
      }
    : {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#fff",
        display: isOpen ? "flex" : "none",
        flexDirection: "column",
        zIndex: 200,
      };

  return (
    <>
      <Box sx={panelStyle}>
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Chat
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {timeline.map((item) => {
            if (item.kind === "message") {
              const message = item.message;
              const isOwn = message.user.id === userIdLogged;

              return (
                <Box
                  key={item.id}
                  sx={{
                    alignSelf: isOwn ? "flex-end" : "flex-start",
                    maxWidth: "80%",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748B",
                      display: "block",
                      mb: 0.5,
                      textAlign: isOwn ? "right" : "left",
                    }}
                  >
                    {message.user.name}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: isOwn ? "#0A6172" : "#F1F5F9",
                      color: isOwn ? "#fff" : "#1E293B",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      wordBreak: "break-word",
                    }}
                  >
                    <Typography variant="body2">{message.text}</Typography>
                  </Box>
                </Box>
              );
            }

            const file = item.file;
            const authorName = resolveFileAuthorName(file, participants);
            const isOwn = isOwnFile(file, userIdLogged, uploadedFileIds);
            const isImage = isImageFile(file.mime_type);
            const isDownloading = downloadingFileIds.has(file.id);
            const canDownload = !isOwn;

            return (
              <Box
                key={item.id}
                sx={{
                  alignSelf: isOwn ? "flex-end" : "flex-start",
                  maxWidth: "85%",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748B",
                    display: "block",
                    mb: 0.5,
                    textAlign: isOwn ? "right" : "left",
                  }}
                >
                  {authorName}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    backgroundColor: isOwn ? "#E6F4F1" : "#F8FAFC",
                    borderRadius: 2,
                    border: "1px solid #E2E8F0",
                    cursor: canDownload && !isDownloading ? "pointer" : "default",
                    "&:hover": canDownload && !isDownloading
                      ? { backgroundColor: "#F1F5F9" }
                      : {},
                  }}
                  onClick={() => {
                    if (canDownload && !isDownloading) {
                      handleDownloadFile(file);
                    }
                  }}
                >
                  {isImage ? (
                    <InsertPhoto sx={{ color: "#0A6172" }} />
                  ) : (
                    <AttachFile sx={{ color: "#64748B" }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.display_name}
                  </Typography>
                  {canDownload ? (
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDownloadFile(file);
                      }}
                      disabled={isDownloading}
                      aria-label={`Baixar ${file.display_name}`}
                    >
                      {isDownloading ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Download fontSize="small" />
                      )}
                    </IconButton>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                      Enviado
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}

          <div ref={messagesEndRef} />
        </Box>

        {isTyping && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
              {isTyping} está digitando...
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #E2E8F0",
          }}
        >
          {!isConnected && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Conectando ao chat...
            </Typography>
          )}
          <Paper
            sx={{
              display: "flex",
              alignItems: "center",
              p: "4px 8px",
              border: "1px solid #E2E8F0",
              boxShadow: "none",
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            />
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || !isConnected}
            >
              {isUploading ? (
                <CircularProgress size={20} />
              ) : (
                <AttachFile sx={{ color: "#64748B" }} />
              )}
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Digite sua mensagem..."
              value={newMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={!isConnected}
            />
            <Divider sx={{ height: 24, mx: 1 }} orientation="vertical" />
            <IconButton
              size="small"
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || !isConnected}
            >
              <Send sx={{ color: newMessage.trim() && isConnected ? "#0A6172" : "#CBD5E1" }} />
            </IconButton>
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default ChatPanel;
